import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CheckboxChipGroup } from '@/components/common/CheckboxChipGroup';
import { DatePicker } from '@/components/common/DatePicker';
import { FieldRow } from '@/components/common/FieldRow';
import { FormHeader } from '@/components/common/FormHeader';
import { FormStatusMessage } from '@/components/common/FormStatusMessage';
import { FormSuccessState } from '@/components/common/FormSuccessState';
import { HoneypotField } from '@/components/common/HoneypotField';
import { SubmitButton } from '@/components/common/SubmitButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { EngagementOptions } from '@/constants/engagements';
import { FieldLimits } from '@/constants/fieldLimits';
import { FormStatus } from '@/constants/formStatus';
import { Routes } from '@/constants/routes';
import { ServiceOptions } from '@/constants/services';
import { useFormFields } from '@/hooks/useFormFields';
import { findFirstInvalidField, getFieldAriaProps, pickTouchedErrors } from '@/lib/formErrors';
import { BudgetOptions, CompanyTypeOptions, ProjectRequestFormContent, ReferralOptions } from '@/pages/GetStarted/data';
import {
  EmptyProjectRequest,
  ProjectRequestFieldIds as FieldIds,
  ProjectRequestFieldOrder,
  ServicesIdPrefix,
  validateProjectRequest,
} from '@/pages/GetStarted/validation';
import { createSubmissionId, submitProjectRequest } from '@/services/inquiryService';

const EngagementSelectOptions = EngagementOptions.map((engagementOption) => ({ value: engagementOption.id, label: engagementOption.title }));
const ServiceChipOptions = ServiceOptions.map((serviceOption) => ({ value: serviceOption.id, label: serviceOption.title }));
const FormSectionClasses = 'border-t border-grid pt-8 first:border-t-0 first:pt-0';
const LegendClasses = 'text-lg font-bold text-graphite';
const FaxFieldId = 'request-fax';

function getEngagementFromQuery(engagementId) {
  return EngagementSelectOptions.some((engagementOption) => engagementOption.value === engagementId) ? engagementId : '';
}

export function ProjectRequestForm({ titleId }) {
  const [searchParams] = useSearchParams();
  const { values, touchedFields, updateField, markTouched, markAllTouched, resetFields, bindInput } = useFormFields({
    ...EmptyProjectRequest,
    engagement: getEngagementFromQuery(searchParams.get('engagement')),
  });
  const [formStatus, setFormStatus] = useState(FormStatus.idle);
  const [failureMessage, setFailureMessage] = useState(null);
  const [submissionId, setSubmissionId] = useState(createSubmissionId);
  const faxInputRef = useRef(null);
  const errors = validateProjectRequest(values);
  const shownErrors = pickTouchedErrors(errors, touchedFields);
  const isSubmitting = formStatus === FormStatus.submitting;

  async function handleSubmit(event) {
    event.preventDefault();
    markAllTouched();
    const firstInvalidField = findFirstInvalidField(errors, ProjectRequestFieldOrder);
    if (firstInvalidField) {
      document.getElementById(FieldIds[firstInvalidField])?.focus();
      return;
    }

    setFormStatus(FormStatus.submitting);
    const submissionFailure = await submitProjectRequest({ ...values, submissionId, fax: faxInputRef.current?.value ?? '' });
    setFailureMessage(submissionFailure);
    setFormStatus(submissionFailure ? FormStatus.failed : FormStatus.succeeded);
  }

  function startOver() {
    resetFields();
    setFailureMessage(null);
    setSubmissionId(createSubmissionId());
    setFormStatus(FormStatus.idle);
  }

  function updateServices(selectedServices) {
    updateField('services', selectedServices);
    markTouched('services');
  }

  if (formStatus === FormStatus.succeeded) {
    return (
      <FormSuccessState
        titleId={titleId}
        title={ProjectRequestFormContent.success.title}
        description={ProjectRequestFormContent.success.description}
        actionLabel={ProjectRequestFormContent.success.actionLabel}
        onAction={startOver}
      >
        <Button asChild variant="ghost">
          <Link to={Routes.home}>{ProjectRequestFormContent.success.homeLinkLabel}</Link>
        </Button>
      </FormSuccessState>
    );
  }

  return (
    <>
      <FormHeader titleId={titleId} title={ProjectRequestFormContent.title} description={ProjectRequestFormContent.description} />
      <form noValidate onSubmit={handleSubmit} aria-busy={isSubmitting} className="mt-8 space-y-8">
        <div className={FormSectionClasses}>
          <fieldset className="space-y-5">
            <legend className={LegendClasses}>{ProjectRequestFormContent.sections.requester}</legend>
            <div className="grid gap-5 sm:grid-cols-2">
              <FieldRow id={FieldIds.name} label="Full name" required error={shownErrors.name}>
                <Input id={FieldIds.name} autoComplete="name" maxLength={FieldLimits.name} {...bindInput('name')} {...getFieldAriaProps(FieldIds.name, shownErrors.name)} />
              </FieldRow>
              <FieldRow id={FieldIds.company} label="Company" required error={shownErrors.company}>
                <Input
                  id={FieldIds.company}
                  autoComplete="organization"
                  maxLength={FieldLimits.company}
                  {...bindInput('company')}
                  {...getFieldAriaProps(FieldIds.company, shownErrors.company)}
                />
              </FieldRow>
              <FieldRow id={FieldIds.email} label="Work email" required error={shownErrors.email}>
                <Input
                  id={FieldIds.email}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  maxLength={FieldLimits.email}
                  {...bindInput('email')}
                  {...getFieldAriaProps(FieldIds.email, shownErrors.email)}
                />
              </FieldRow>
              <FieldRow id={FieldIds.phone} label="Phone" error={shownErrors.phone}>
                <Input
                  id={FieldIds.phone}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={FieldLimits.phone}
                  {...bindInput('phone')}
                  {...getFieldAriaProps(FieldIds.phone, shownErrors.phone)}
                />
              </FieldRow>
            </div>
          </fieldset>
        </div>

        <div className={FormSectionClasses}>
          <fieldset className="space-y-5">
            <legend className={LegendClasses}>{ProjectRequestFormContent.sections.company}</legend>
            <div className="grid gap-5 sm:grid-cols-2">
              <FieldRow id={FieldIds.companyType} label="Type of company" required error={shownErrors.companyType}>
                <Select
                  id={FieldIds.companyType}
                  options={CompanyTypeOptions}
                  placeholder="Select a company type"
                  value={values.companyType}
                  onChange={(selectedType) => updateField('companyType', selectedType)}
                  onBlur={() => markTouched('companyType')}
                  {...getFieldAriaProps(FieldIds.companyType, shownErrors.companyType)}
                />
              </FieldRow>
              <FieldRow id={FieldIds.serviceArea} label="Where you work" required error={shownErrors.serviceArea}>
                <Input
                  id={FieldIds.serviceArea}
                  autoComplete="off"
                  placeholder={ProjectRequestFormContent.serviceAreaPlaceholder}
                  maxLength={FieldLimits.serviceArea}
                  {...bindInput('serviceArea')}
                  {...getFieldAriaProps(FieldIds.serviceArea, shownErrors.serviceArea)}
                />
              </FieldRow>
              <FieldRow id={FieldIds.website} label="Company website" error={shownErrors.website}>
                <Input
                  id={FieldIds.website}
                  type="url"
                  inputMode="url"
                  autoComplete="url"
                  placeholder={ProjectRequestFormContent.websitePlaceholder}
                  maxLength={FieldLimits.website}
                  {...bindInput('website')}
                  {...getFieldAriaProps(FieldIds.website, shownErrors.website)}
                />
              </FieldRow>
            </div>
          </fieldset>
        </div>

        <div className={FormSectionClasses}>
          <fieldset className="space-y-5">
            <legend className={LegendClasses}>{ProjectRequestFormContent.sections.needs}</legend>
            <div className="grid gap-5 sm:grid-cols-2">
              <FieldRow id={FieldIds.engagement} label="How would you like to work with us?" required error={shownErrors.engagement}>
                <Select
                  id={FieldIds.engagement}
                  options={EngagementSelectOptions}
                  placeholder="Select an option"
                  value={values.engagement}
                  onChange={(selectedEngagement) => updateField('engagement', selectedEngagement)}
                  onBlur={() => markTouched('engagement')}
                  {...getFieldAriaProps(FieldIds.engagement, shownErrors.engagement)}
                />
              </FieldRow>
              <FieldRow id={FieldIds.budget} label="Monthly budget">
                <Select
                  id={FieldIds.budget}
                  options={BudgetOptions}
                  placeholder="Select a range"
                  value={values.budget}
                  onChange={(selectedBudget) => updateField('budget', selectedBudget)}
                />
              </FieldRow>
            </div>
            <CheckboxChipGroup
              idPrefix={ServicesIdPrefix}
              legend="Services you're interested in"
              required
              options={ServiceChipOptions}
              selectedValues={values.services}
              onChange={updateServices}
              error={shownErrors.services}
            />
          </fieldset>
        </div>

        <div className={FormSectionClasses}>
          <fieldset className="space-y-5">
            <legend className={LegendClasses}>{ProjectRequestFormContent.sections.timing}</legend>
            <div className="grid gap-5 sm:grid-cols-2">
              <FieldRow id={FieldIds.deadline} label="I need this done by" hint={ProjectRequestFormContent.deadlineHint}>
                <DatePicker
                  id={FieldIds.deadline}
                  value={values.deadline}
                  onChange={(selectedDate) => updateField('deadline', selectedDate)}
                  disablePastDates
                  {...getFieldAriaProps(FieldIds.deadline, null, true)}
                />
              </FieldRow>
            </div>
          </fieldset>
        </div>

        <div className={FormSectionClasses}>
          <fieldset className="space-y-5">
            <legend className={LegendClasses}>{ProjectRequestFormContent.sections.details}</legend>
            <FieldRow id={FieldIds.goals} label="What do you want your marketing to do?" required error={shownErrors.goals}>
              <Textarea
                id={FieldIds.goals}
                placeholder={ProjectRequestFormContent.goalsPlaceholder}
                maxLength={FieldLimits.goals}
                {...bindInput('goals')}
                {...getFieldAriaProps(FieldIds.goals, shownErrors.goals)}
              />
            </FieldRow>
            <div className="grid gap-5 sm:grid-cols-2">
              <FieldRow id={FieldIds.referral} label="How did you hear about us?">
                <Select
                  id={FieldIds.referral}
                  options={ReferralOptions}
                  placeholder="Select an option"
                  value={values.referral}
                  onChange={(selectedReferral) => updateField('referral', selectedReferral)}
                />
              </FieldRow>
            </div>
          </fieldset>
        </div>

        <HoneypotField id={FaxFieldId} inputRef={faxInputRef} />
        <FormStatusMessage message={formStatus === FormStatus.failed ? failureMessage : null} />
        <div className="flex flex-col gap-4 border-t border-grid pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-steel">{ProjectRequestFormContent.privacyNote}</p>
          <SubmitButton
            isSubmitting={isSubmitting}
            label={ProjectRequestFormContent.submitLabel}
            submittingLabel={ProjectRequestFormContent.submittingLabel}
            className="w-full sm:w-auto"
          />
        </div>
      </form>
    </>
  );
}